import ko from 'knockout';
import uploadValidation from '@/App/uploadValidation';
import logger from '@/Vendor/inv-logger/logger';
import uploadRepository from '@/App/Repositories/uploadRepository';
import responseRepository from '@/App/Repositories/responseRepository';
import resourceHelper from '@/Vendor/inv-resource-helper/resourceHelper';
import fileUpload from '@/Vendor/inv-file-upload/fileUpload';
import contextData from '@/App/contextData';
import constants from '@/App/constants';
import 'knockout.validation';
import '@/App/Bindings/showModalBindings';
import '@/App/Bindings/beforeUnloadBinding';
import '@/App/Bindings/dynamicFormBinding';
import template from './customResponseForm.html';
import UploadStatus from '@/App/Types/enums/UploadStatus';
import uploadManager from '@/Vendor/inv-file-upload/utils/uploadManager';

export function CustomResponseForm(params) {

  var self = this;

  self.languageCode = contextData.languageCode;

  self.helpLinkTitle = resourceHelper.getString('HelpLinkTitle');
  self.confirmResponseModalCloseButtonLabel = resourceHelper.getString('ConfirmResponseModalCloseButton');
  self.leaveWarning = resourceHelper.getString('ChangesMayNotBeSaved');
  self.hasOpeningText = false;

  self.responseType = params.responseMetadata.responseParentType;
  self.responseTypes = constants.responseParentType;

  self.responsePublicReference = params.responsePublicReference;

  self.response = ko.observable({});
  self.responseFormDefinition = null;
  self.dataChangedCallback = args => {
    self.response(args.data);
  };

  self.responseAllowUploads = ko.observable(true);
  self.uploads = ko.observableArray([]);
  self.uploadClientErrorResourceObject = ko.observable(null);
  self.allUploadsComplete = uploadManager.allUploadsComplete;

  self.responseConfirmationText = ko.observable('');
  self.responseConfirmationTitleTemplate = ko.observable('');
  self.responseConfirmationTitle = ko.computed(function () {
    return self.responseConfirmationTitleTemplate().replace('{uploadedFilesCount}', self.uploads().length);
  });

  self.uploadAlreadyRunningTitle = ko.observable('');

  self.showSubmitPrompt = ko.observable(false); // Flags if the response summary and T&Cs modal is displayed
  self.showRunningUploadsModal = ko.observable(false); // Flags show modal infor if uploads already heppening in other window is displayed
  self.showRunningUploadsModalTimeElapsedTriggerMinSec =
    contextData.portalSettings.showRunningUploadsModalTimeElapsedTriggerMinSec; // time in seconds
  self.showRunningUploadsModalTimeElapsedTriggerMaxSec =
    contextData.portalSettings.showRunningUploadsModalTimeElapsedTriggerMaxSec; // time in seconds
  self.formSubmitted = ko.observable(false); // Flags if submit Ajax request in progress

  self.areUploadedFilesLoaded = ko.observable(false); // Flags if retrieve upload files details Ajax request completed

  self.isAllDataLoaded = ko.computed(function () { // Flags if all data for response is loaded
    return !self.responseAllowUploads() || self.areUploadedFilesLoaded();
  });

  self.hasPendingUploads = ko.pureComputed(function () {
    return uploadValidation.getValidationResourceKey(self.uploads(), true) === 'UploadsStillInProgress';
  });

  self.allFilesUploaded = ko.pureComputed(function () { // Flags if there are uploads and they are all complete
    return self.uploads().length > 0 && !self.hasPendingUploads();
  });

  self.addFileUploads = function (fileList) {
    var newUploads;

    var resourceObject = uploadValidation.getValidationResourceKeyForNewUploads(fileList, self.uploads());
    self.uploadClientErrorResourceObject(resourceObject);

    if (resourceObject) {
      var validationFailureText = getLocalizedString(resourceObject);
      logger.error('UploadFailure', validationFailureText);
      return;
    }

    newUploads = uploadRepository.addUploads(fileList, self.responsePublicReference, self.addUploadsFailed);
    self.uploads.unshift(...newUploads); // Add newest items to the top of the list
  };

  self.addUploadsFailed = function (e) {
    if (e.errorMessages) {
      self.serverErrors(e.errorMessages);
      return;
    }

    const resourceKey = e.status === 0 ? 'UnhandledErrorOffline' : 'UnhandledError';
    logger.error(resourceKey, 'An unexpected error occurred while attempting to add file uploads.', e);
  };

  /**
   * Calls the API to remove an upload. The API call could fail (if offline for example) and in this
   * scenario the upload must not be removed from the UI as this will invalidate the client-side
   * validation as the upload still exists.
   * @param {UploadModel} upload
   */
  self.removeUpload = function (upload) {
    upload.setIsBeingRemoved(true);

    uploadRepository.deleteUpload(upload)
      .then(() => {
        self.uploads.remove(upload);
      })
      .catch(function (jqXhr) {
        upload.setIsBeingRemoved(false); // Re-instate the upload exactly as it was before removal attempt

        if (!jqXhr.errorHasBeenLogged) {
          const resourceKey = jqXhr.status === 0 ? 'UnhandledErrorOffline' : 'UnhandledError';
          logger.error(resourceKey, 'Failed to delete the file upload.', jqXhr);
        }
      });
  };

  self.confirmSubmit = function () {
    let someValidationErrors = false;

    if (self.responseAllowUploads()) {
      var validationFailureResourceObject = uploadValidation.getValidationResourceKey(self.uploads(), true);
      if (validationFailureResourceObject) {
        someValidationErrors = true;
        self.uploadClientErrorResourceObject(validationFailureResourceObject);
      }
    }

    self.dynamicForm.validate().then(function (isValid) {
      someValidationErrors |= !isValid;

      if (someValidationErrors) {
        return;
      }

      self.uploadClientErrorResourceObject(null);
      self.showSubmitPrompt(true);
    });
  };

  self.submitForm = function () {

    self.showSubmitPrompt(false);
    self.formSubmitted(true);

    var responseDetails = {
      dynamicFormData: JSON.stringify(self.response())
    };

    return uploadRepository.updateUploads(self.uploads())
      .then(function () {
        return responseRepository.completeResponse(self.responsePublicReference, responseDetails);
      })
      .then(function () {
        navigateToResponseCompletePage(self.responsePublicReference);
      })
      .catch(function (jqXhr) {
        self.formSubmitted(false);

        logger.logAndThrowUntranslatedError(
          'An error occurred on submitting a response. Please try again later.',
          'Unable to submit a response',
          jqXhr);
      });
  };

  function getResponseUploads(params) {
    var responseAllowUploads = params.responseMetadata.responseAllowUploads;
    if (!responseAllowUploads) {
      return Promise.resolve();
    }

    return uploadRepository.getUploadsByResponsePublicReference(params.responsePublicReference)
      .then(function (uploadList) {

        ko.utils.arrayPushAll(self.uploads, uploadList);

        self.areUploadedFilesLoaded(true);

        return Promise.resolve();
      })
      .catch(function (jqXhr) {

        logger.logAndThrowUntranslatedError(
          'An error occurred on loading the response uploads. Please try again later.',
          'Unable to get the response uploads',
          jqXhr);

        return Promise.reject(jqXhr);
      });
  }

  function navigateToResponseCompletePage(responsePublicReference) {
    window.location.href = '/' + self.languageCode + '/response/' + responsePublicReference;
  }

  function getLocalizedString(resTextObject) {
    var value = resourceHelper.getString(resTextObject.key);
    value = resourceHelper.replacePlaceholders(value, resTextObject.params);

    return value;
  }

  function initResponseForm(params) {
    var responseMetadata = params.responseMetadata;

    setResponseConfirmationMessage(responseMetadata);

    self.responseAllowUploads(responseMetadata.responseAllowUploads);
    self.responseFormDefinition = JSON.parse(responseMetadata.responseFormDefinition);
    self.hasOpeningText = responseMetadata.responseFormDefinition.indexOf('form-opening-text') !== -1;

    return Promise.resolve(params);
  }

  function setResponseConfirmationMessage(responseMetadata) {
    var responseConfirmationMessageTemplate;

    if (responseMetadata.responseParentType === constants.responseParentType.request) {
      responseConfirmationMessageTemplate = responseMetadata.responseAllowUploads ?
        resourceHelper.getString('ConfirmRequestResponseModalMessage') :
        resourceHelper.getString('ConfirmRequestResponseWithoutUploadsModalMessage');
      responseConfirmationMessageTemplate = responseConfirmationMessageTemplate
        .replace('{requestorFullName}', responseMetadata.requestorFullName);
    } else if (self.responseType === constants.responseParentType.appeal) {
      responseConfirmationMessageTemplate = responseMetadata.responseAllowUploads ?
        resourceHelper.getString('ConfirmAppealResponseModalMessage') :
        resourceHelper.getString('ConfirmAppealResponseWithoutUploadsModalMessage');
    }

    self.responseConfirmationTitleTemplate(responseConfirmationMessageTemplate);
    self.responseConfirmationText(responseMetadata.responseConfirmationText);

    const uploadAlreadyRunningTitle = responseMetadata.uploadAlreadyRunningTitle ?
      responseMetadata.uploadAlreadyRunningTitle :
      resourceHelper.getString('UploadAlreadyRunningTitle');

    self.uploadAlreadyRunningTitle(uploadAlreadyRunningTitle);
  }


  function initFileUploadControl(params) {
    var responseMetadata = params.responseMetadata;

    if (responseMetadata.responseParentType === constants.responseParentType.request) {
      self.maximumUploadSize = contextData.portalSettings.maximumRequestUploadSize;
      self.maximumFilesUploads = contextData.portalSettings.maximumRequestFilesUploads;

    } else if (responseMetadata.responseParentType === constants.responseParentType.appeal) {
      self.maximumUploadSize = contextData.portalSettings.maximumAppealUploadSize;
      self.maximumFilesUploads = contextData.portalSettings.maximumAppealFilesUploads;
    }

    self.fileUploadSelectorOptions = {
      maximumUploadSize: self.maximumUploadSize,
      addFileUploadsCallback: self.addFileUploads,
      maximumFilesUploads: self.maximumFilesUploads
    };

    uploadValidation.init({
      maxFileSize: self.maximumUploadSize,
      fileNumberLimit: self.maximumFilesUploads,
      blacklistedFileTypes: contextData.portalSettings.blacklistedFileTypes
    });

    fileUpload.init({
      languageCode: self.languageCode,
      fileUploadServiceRootUrl: contextData.portalSettings.fileUploadServiceRootUrl,
      maximumChunkSize: contextData.portalSettings.maximumChunkSize,
      maximumConcurrentUploads: contextData.portalSettings.maximumConcurrentUploads,
      maximumFilesUploads: self.maximumFilesUploads
    });

    return Promise.resolve(params);
  }


  function diffInSeconds(t1ms, t2ms) {
    return Math.floor((t1ms - t2ms) / 1000);
  }


  /**
   *  Check if in the new window/tab, and if there are any uploads in progress and a small amount of time has elapsed show modal dialog
   * */
  function showConcurrentUploadRunning() {
    const uploads = self.uploads();
    const timeNow = Date.now();
    let maxDurationDifference = 0;
    let anyFailedUploads = false;

    // if variables not set - return
    if (self.showRunningUploadsModalTimeElapsedTriggerMinSec === 0 ||
      self.showRunningUploadsModalTimeElapsedTriggerMaxSec === 0) {
      return;
    }

    for (let i = 0; i < uploads.length; i++) {
      // any pending or uploading files are marked as failed on serverside
      if (uploads[i].statusName() === UploadStatus.failed) {
        anyFailedUploads = true;
        const timeAdded = new Date(uploads[i].addedTimestamp).getTime();
        const duration = diffInSeconds(timeNow, timeAdded);
        maxDurationDifference = duration > maxDurationDifference ? duration : maxDurationDifference;
      }
    }

    if (anyFailedUploads === true &&
      maxDurationDifference > self.showRunningUploadsModalTimeElapsedTriggerMinSec &&
      maxDurationDifference <= self.showRunningUploadsModalTimeElapsedTriggerMaxSec) {
      self.showRunningUploadsModal(true);
    }
  }

  initResponseForm(params);
  initFileUploadControl(params);
  getResponseUploads(params)
    .then(showConcurrentUploadRunning);
}

// The default export returns the component details object to register with KO
export default { viewModel: CustomResponseForm, template: template };
